// components/Editor.js
import React, { useState, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from "@clerk/clerk-react";

const GlobalEditorStyles = createGlobalStyle`
  .medium-editor {
    font-family: 'Clash Grotesk', sans-serif;
    line-height: 1.6;
    color: #184A2C;
  }

  .medium-editor h1, .medium-editor h2, .medium-editor h3 {
    font-family: 'Clash Grotesk', sans-serif;
    color: #034C53;
    margin: 20px 0 10px 0;
  }

  .medium-editor h1 {
    font-size: 32px;
    font-weight: 600;
  }

  .medium-editor h2 {
    font-size: 24px;
    font-weight: 500;
  }

  .medium-editor h3 {
    font-size: 20px;
    font-weight: 500;
  }

  .medium-editor p {
    margin-bottom: 16px;
    font-size: 18px;
  }

  .medium-editor blockquote {
    border-left: 4px solid #66BFBF;
    padding-left: 20px;
    margin: 20px 0;
    font-style: italic;
    color: #034C53;
    opacity: 0.8;
  }

  .medium-editor pre {
    background: #f6f8fa;
    border: 1px solid #e1e4e8;
    border-radius: 6px;
    padding: 16px;
    overflow-x: auto;
    margin: 20px 0;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 14px;
  }

  .medium-editor code {
    background: #f6f8fa;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 14px;
    color: #034C53;
  }

  .medium-editor a {
    color: #66BFBF;
    text-decoration: underline;
  }

  .medium-editor ul, .medium-editor ol {
    margin: 16px 0;
    padding-left: 30px;
  }

  .medium-editor li {
    margin-bottom: 8px;
  }
`;

const EditorContainer = styled.div`
  max-width: 740px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #e9ecef;
`;

const ToolbarButton = styled.button`
  background: ${props => props.active ? '#66BFBF' : 'transparent'};
  color: ${props => props.active ? 'white' : '#034C53'};
  border: 1px solid ${props => props.active ? '#66BFBF' : '#dee2e6'};
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  font-family: 'Clash Grotesk', sans-serif;

  &:hover {
    background: ${props => props.active ? '#5ab0b0' : '#e9ecef'};
    border-color: ${props => props.active ? '#5ab0b0' : '#adb5bd'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditorContent = styled.div`
  min-height: 400px;
  padding: 40px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: white;
  font-size: 18px;
  line-height: 1.6;
  font-family: 'Clash Grotesk', sans-serif;
  color: #184A2C;

  &:focus {
    outline: none;
    border-color: #66BFBF;
    box-shadow: 0 0 0 2px rgba(102, 191, 191, 0.1);
  }

  &[placeholder]:empty:before {
    content: attr(placeholder);
    color: #6c757d;
    font-style: italic;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const SaveButton = styled.button`
  background: #66BFBF;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  font-family: 'Clash Grotesk', sans-serif;
  transition: all 0.2s ease;

  &:hover {
    background: #5ab0b0;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const CancelButton = styled.button`
  background: transparent;
  color: #034C53;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  font-family: 'Clash Grotesk', sans-serif;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    border-color: #adb5bd;
  }
`;

const TitleInput = styled.input`
  width: 100%;
  padding: 16px;
  font-size: 32px;
  font-weight: 600;
  border: none;
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 20px;
  font-family: 'Clash Grotesk', sans-serif;
  color: #034C53;
  background: transparent;

  &:focus {
    outline: none;
    border-bottom-color: #66BFBF;
  }

  &::placeholder {
    color: #6c757d;
    font-weight: 500;
  }
`;

const LinkModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  min-width: 400px;
  border: 1px solid #e9ecef;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 16px;
  font-family: 'Clash Grotesk', sans-serif;
  margin-bottom: 16px;

  &:focus {
    outline: none;
    border-color: #66BFBF;
    box-shadow: 0 0 0 2px rgba(102, 191, 191, 0.1);
  }
`;

const ModalButton = styled.button`
  background: #66BFBF;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Clash Grotesk', sans-serif;
  margin-right: 8px;

  &:hover {
    background: #5ab0b0;
  }

  &:last-child {
    background: transparent;
    color: #034C53;
    
    &:hover {
      background: #f8f9fa;
    }
  }
`;

const StatusMessage = styled.div`
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-family: 'Clash Grotesk', sans-serif;
  text-align: center;
  
  ${props => props.type === 'success' && `
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  `}
  
  ${props => props.type === 'error' && `
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `}
`;

const Editor = ({ onSave, initialData }) => {
  const editorRef = useRef(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [title, setTitle] = useState(initialData?.title || '');
  const [content, setContent] = useState(initialData?.content || '');
  const [isSaving, setIsSaving] = useState(false);
  const { user } = useUser();
  const userId = user?.id || 'anonymous';
  const [status, setStatus] = useState({ message: '', type: '' });

  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
  };

  const formatBlock = (tagName) => {
    document.execCommand('formatBlock', false, `<${tagName}>`);
  };

  const insertLink = () => {
    const selection = window.getSelection();
    if (selection.toString().trim()) {
      setLinkText(selection.toString());
      setShowLinkModal(true);
    }
  };

  const handleInsertLink = () => {
    if (linkUrl) {
      executeCommand('createLink', linkUrl);
      setShowLinkModal(false);
      setLinkUrl('');
      setLinkText('');
    }
  };

  const insertCode = () => {
    const selection = window.getSelection();
    if (selection.toString().trim()) {
      executeCommand('formatBlock', '<pre>');
    } else {
      const pre = document.createElement('pre');
      const code = document.createElement('code');
      code.textContent = '// Your code here';
      pre.appendChild(code);
      document.execCommand('insertHTML', false, pre.outerHTML);
    }
  };

  const handleContentChange = () => {
    setContent(editorRef.current.innerHTML);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setStatus({ message: 'Please add a title', type: 'error' });
      return;
    }

    if (!content.trim() || content === '<br>') {
      setStatus({ message: 'Content cannot be empty', type: 'error' });
      return;
    }

    setIsSaving(true);
    setStatus({ message: '', type: '' });

    try {
      const articleData = {
        approved:false,
        userId,
        title: title.trim(),
        content: content, 
        createdAt: new Date(),
        updatedAt: new Date(),
        wordCount: content.replace(/<[^>]*>/g, '').split(/\s+/).length,
      };

      let result;
      if (initialData?.id) {
        // Update existing article
        const docRef = doc(db, 'pending-articles', initialData.id);
        await updateDoc(docRef, {
          ...articleData,
          updatedAt: new Date(),
        });
        result = { id: initialData.id };
      } else {
        // Create new article
        const docRef = await addDoc(collection(db, 'pending-articles'), articleData);
        result = { id: docRef.id };
      }

      setStatus({ 
        message: initialData?.id ? 'Article updated successfully!' : 'Article submitted for review!', 
        type: 'success' 
      });

      if (onSave) {
        onSave({ id: result.id, ...articleData });
      }

      // Clear form if it's a new article
      if (!initialData?.id) {
        setTitle('');
        setContent('');
        editorRef.current.innerHTML = '';
      }

    } catch (error) {
      console.error('Error saving article:', error);
      setStatus({ 
        message: 'Error saving article. Please try again.', 
        type: 'error' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (initialData?.id) {
      // Reset to initial data
      setTitle(initialData.title);
      setContent(initialData.content);
      editorRef.current.innerHTML = initialData.content;
    } else {
      // Clear everything
      setTitle('');
      setContent('');
      editorRef.current.innerHTML = '';
    }
    setStatus({ message: '', type: '' });
  };

  return (
    <>
      <GlobalEditorStyles />
      <EditorContainer className="medium-editor">
        {status.message && (
          <StatusMessage type={status.type}>
            {status.message}
          </StatusMessage>
        )}

        <TitleInput
          type="text"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <Toolbar>
          <ToolbarButton onClick={() => formatBlock('h1')}>H1</ToolbarButton>
          <ToolbarButton onClick={() => formatBlock('h2')}>H2</ToolbarButton>
          <ToolbarButton onClick={() => formatBlock('h3')}>H3</ToolbarButton>
          <ToolbarButton onClick={() => formatBlock('p')}>Paragraph</ToolbarButton>
          <ToolbarButton onClick={() => formatBlock('blockquote')}>Quote</ToolbarButton>
          
          <div style={{ width: '1px', background: '#dee2e6', margin: '0 8px' }}></div>
          
          <ToolbarButton onClick={() => executeCommand('bold')}>
            <strong>B</strong>
          </ToolbarButton>
          <ToolbarButton onClick={() => executeCommand('italic')}>
            <em>I</em>
          </ToolbarButton>
          <ToolbarButton onClick={() => executeCommand('underline')}>
            <u>U</u>
          </ToolbarButton>
          
          <div style={{ width: '1px', background: '#dee2e6', margin: '0 8px' }}></div>
          
          <ToolbarButton onClick={insertLink}>Link</ToolbarButton>
          <ToolbarButton onClick={insertCode}>Code</ToolbarButton>
          
          <div style={{ width: '1px', background: '#dee2e6', margin: '0 8px' }}></div>
          
          <ToolbarButton onClick={() => executeCommand('insertUnorderedList')}>
            • List
          </ToolbarButton>
          <ToolbarButton onClick={() => executeCommand('insertOrderedList')}>
            1. List
          </ToolbarButton>
        </Toolbar>

        <EditorContent
          ref={editorRef}
          contentEditable
          placeholder="Start writing your story..."
          onInput={handleContentChange}
          dangerouslySetInnerHTML={initialData?.content ? { __html: initialData.content } : undefined}
        />

        <ActionButtons>
          <CancelButton onClick={handleCancel}>
            {initialData?.id ? 'Reset' : 'Clear'}
          </CancelButton>
          <SaveButton onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : initialData?.id ? 'Update Article' : 'Publish Article'}
          </SaveButton>
        </ActionButtons>

        {showLinkModal && (
          <>
            <ModalOverlay onClick={() => setShowLinkModal(false)} />
            <LinkModal>
              <h3 style={{ marginBottom: '16px', color: '#034C53' }}>Insert Link</h3>
              <ModalInput
                type="text"
                placeholder="Link text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
              />
              <ModalInput
                type="url"
                placeholder="https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
              <div style={{ textAlign: 'right' }}>
                <ModalButton onClick={handleInsertLink}>Insert</ModalButton>
                <ModalButton onClick={() => setShowLinkModal(false)}>Cancel</ModalButton>
              </div>
            </LinkModal>
          </>
        )}
      </EditorContainer>
    </>
  );
};

export default Editor;